import { getDashboardSummary, getDauTrend } from "../api/dashboard";
import { useAsync } from "../hooks/useAsync";
import { formatBeijingTime } from "../utils/datetime";

/**
 * 运营看板,对应产品文档 §8.2。
 *
 * v2.10:原"成本趋势(近 30 天)"图表位置改为"每日活跃用户数"(Active 指
 * subscription_status=ACTIVE)。成本趋势本身没有删除,移到了新增的独立
 * Cost Summary 页面(见 CostSummaryPage.tsx),与"每一个纳管账号的
 * Month-to-date 费用"一起展示。
 */
export function DashboardPage() {
  const summary = useAsync(() => getDashboardSummary(), []);
  const dauTrend = useAsync(() => getDauTrend(30), []);

  if (summary.loading) return <div>加载中...</div>;
  if (summary.error) return <div className="error-banner">{summary.error}</div>;
  if (!summary.data) return null;

  const d = summary.data;
  // §6:激活率按"订阅状态=Active"的数量/占比展示(与 activation_health.activation_rate
  // 语义不同——那个是"激活状态=ACTIVE 的用户 ÷ 未取消订阅的用户",这里按需求
  // 改成更直观的"订阅状态=Active 的数量和占用户总数的百分比"),直接汇总各账号
  // 的 active_count,不需要新增后端接口。分母用 total_users(不含已退订),
  // 与"用户总数"卡片的口径保持一致。
  const activeSubscriptionCount = d.account_summaries.reduce((sum, a) => sum + a.active_count, 0);
  const activeSubscriptionRate = d.total_users > 0 ? (activeSubscriptionCount / d.total_users) * 100 : 0;

  return (
    <div>
      <h2 className="section-title">运营看板</h2>

      <div className="card-grid">
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>用户总数</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{d.total_users}</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>激活率(订阅状态=Active)</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            {activeSubscriptionCount} / {d.total_users}
            <span style={{ fontSize: 16, fontWeight: 400, marginLeft: 6, color: "#64748b" }}>
              ({activeSubscriptionRate.toFixed(1)}%)
            </span>
          </div>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>本月预估费用(USD)</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            ${d.cost_estimate.estimated_monthly_cost_usd.toLocaleString()}
          </div>
          {d.cost_estimate.unknown_tier_pricing.length > 0 && (
            <div style={{ fontSize: 12, color: "#b45309" }}>
              不含未核实定价层级: {d.cost_estimate.unknown_tier_pricing.join(", ")}
            </div>
          )}
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: "#64748b" }}>退订数量</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{d.unsubscribed_count}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-title">层级分布</div>
        {/* 数量口径:不含已退订(CANCELED)的用户,即 Active + Pending,
            与上方"用户总数"卡片口径一致。额外展示 Active/Pending/退订数
            三个子维度,退订数单独列出但不计入"数量"合计。 */}
        <table>
          <thead>
            <tr>
              <th>层级</th>
              <th>数量</th>
              <th>Active</th>
              <th>Pending</th>
              <th>退订数</th>
            </tr>
          </thead>
          <tbody>
            {d.tier_distribution_overall.map((t) => (
              <tr key={t.tier}>
                <td>{t.tier}</td>
                <td>{t.count}</td>
                <td>{t.active_count}</td>
                <td>{t.pending_count}</td>
                <td>{t.canceled_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="section-title">按账号维度</div>
        {/* §8.2 v2.10:"退订数"口径改为统计 subscription_status=CANCELED,
            与原本就存在的 canceled_count 是同一个数字,因此这里合并为一列,
            不再重复展示(旧版本"人工处理"列统计的是 needs_manual_count,
            现按需求已换成退订口径,不再单独保留)。 */}
        <table>
          <thead>
            <tr>
              <th>账号 ID</th>
              <th>用户数</th>
              <th>Active</th>
              <th>Pending</th>
              <th>退订数</th>
            </tr>
          </thead>
          <tbody>
            {d.account_summaries.map((a) => (
              <tr key={a.account_id}>
                <td>{a.account_id}</td>
                <td>{a.total_users}</td>
                <td>{a.active_count}</td>
                <td>{a.pending_count}</td>
                <td>{a.canceled_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="section-title">到期预警(未来 7 天)</div>
        {d.expiry_warnings_7d.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>账号</th>
                <th>邮箱</th>
                <th>层级</th>
                <th>到期日</th>
                <th>剩余天数</th>
              </tr>
            </thead>
            <tbody>
              {d.expiry_warnings_7d.map((w) => (
                <tr key={`${w.account_id}#${w.email}`}>
                  <td>{w.account_id}</td>
                  <td>{w.email}</td>
                  <td>{w.tier}</td>
                  <td>{w.subscription_end_at}</td>
                  <td>{w.days_remaining}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="section-title">闲置用户(长期无Active记录)</div>
        {d.idle_users.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>账号</th>
                <th>邮箱</th>
                <th>层级</th>
                <th>创建时间</th>
                <th>PENDING 天数</th>
              </tr>
            </thead>
            <tbody>
              {d.idle_users.map((u) => (
                <tr key={`${u.account_id}#${u.email}`}>
                  <td>{u.account_id}</td>
                  <td>{u.email}</td>
                  <td>{u.tier}</td>
                  <td>{u.created_at ? formatBeijingTime(u.created_at) : "无数据"}</td>
                  <td>{u.days_pending ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="section-title">每日活跃用户数(近 30 天)</div>
        {/* v2.10:Active 指 subscription_status=ACTIVE。功能刚上线时历史快照
            数据不足 30 天,趋势图会只显示上线以后的天数,这是新指标从零开始
            积累历史数据的必然结果,不是缺陷,无法补出从未运行过的历史数据。 */}
        {dauTrend.loading && <p>加载中...</p>}
        {dauTrend.data && dauTrend.data.data_points.length === 0 && (
          <p style={{ color: "#64748b", fontSize: 13 }}>暂无历史快照数据(功能刚上线,需等待每日定时任务积累数据)</p>
        )}
        {dauTrend.data && dauTrend.data.data_points.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>日期</th>
                <th>活跃用户数</th>
              </tr>
            </thead>
            <tbody>
              {dauTrend.data.data_points.map((p) => (
                <tr key={p.date}>
                  <td>{p.date}</td>
                  <td>{p.active_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
